import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ApAlertService } from 'webcommon/core-ui';
import { FcGenericItemModalBaseComponent } from '../../generic/fc-generic-item-modal-base.component';
import { FcListValidationService, FormGroupArray } from '../../generic/fc-list-validation/fc-list-validation.service';
import { FcModifyPharmacyClearinghouseListItemDto, FcPharmacyClearinghouseListItemDto } from 'webcommon/shared';
import { FcItemModalMode } from '../../../models';
import { FormGroupExtended } from '../../generic';

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'fc-pharmacy-clearinghouse-list-modal',
  templateUrl: './fc-pharmacy-clearinghouse-list-modal.component.html',
  styleUrls: ['./fc-pharmacy-clearinghouse-list-modal.component.less']
})
export class FcPharmacyClearinghouseModalComponent extends FcGenericItemModalBaseComponent implements OnInit {
  private static readonly faxName: string = 'Fax';
  public override baseId = 'fc-pharmacy-clearinghouse-list-modal';
  public isFax = true;

  // List Item with default value
  public listItem: FcPharmacyClearinghouseListItemDto = {
    Id: '',
    ExternalID: '',
    Name: '',
    ClinicName: '',
    SenderID: '',
    Notes: '',
    Inactive: false,
    UserDefined: true,
    Providers: [],
    InactivateFaxOnly: false,
    InterfaceDetails: {
        TestMode: true,
        ProductionModeSettings: {
            GatewayUrl: '',
            GatewayLoginID: '',
            GatewayPassword: '',
            MailboxUrl: '',
            MailboxName: '',
            MailboxPassword: '',
        },
        TestModeSettings: {
            GatewayUrl: '',
            GatewayLoginID: '',
            GatewayPassword: '',
            MailboxUrl: '',
            MailboxName: '',
            MailboxPassword: '',
        }
    }
  };

  // Form Controls
  public externalIdControl: FormControl = new FormControl('');
  public nameControl: FormControl = new FormControl('');
  public clinicNameControl: FormControl = new FormControl('');
  public clinicIDControl: FormControl = new FormControl('');
  public notesControl: FormControl = new FormControl('');
  public inactiveControl: FormControl = new FormControl('');
  public userDefinedControl: FormControl = new FormControl('');

  public providersGroup: FormGroup = new FormGroup({});
  public interfaceDetailsGroup: FormGroup = new FormGroup({});
  public managePharmacyGroup: FormGroupExtended = new FormGroupExtended({});

  // Form Group
  public form = new FormGroup({
    externalId: this.externalIdControl,
    name: this.nameControl,
    clinicName: this.clinicNameControl,
    clinicID: this.clinicIDControl,
    notes: this.notesControl,
    inactive: this.inactiveControl,
    userDefined: this.userDefinedControl,
    providers: this.providersGroup,
    interfaceDetails: this.interfaceDetailsGroup,
    managePharmacy: this.managePharmacyGroup,
  });

  override ngOnInit() {
    super.ngOnInit();

    // Load List Item and set default validation rules
    this.loadListItem({
      ExternalID: {
        MaxLength: 25,
      },
      Name: {
        IsRequired: true,
        MaxLength: 50,
      },
      ClinicName: {
        IsRequired: true,
        MaxLength: 35
      },
      ClinicID: {
        IsRequired: true,
        MaxLength: 50,
      },
      Notes: {
        MaxLength: 65535,
      },
    });

    if (this.fcModalItemContext.mode === FcItemModalMode.Modify) {
      if (this.fcModalItemContext?.selectedFindItemName === FcPharmacyClearinghouseModalComponent.faxName) {
        this.isFax = true;
      } else {
        this.isFax = false;
      }
    }
  }

  public setForm(): void {
    // External ID Control
    this.externalIdControl.setValidators(this.fcListValidation.getValidators('ExternalID'));
    this.externalIdControl.setValue(this.listItem.ExternalID);

    // Clinic Name Control
    this.clinicNameControl.setValidators(this.fcListValidation.getValidators('ClinicName'));
    this.clinicNameControl.setValue(this.listItem.ClinicName);

    // Clinic ID Control
    this.clinicIDControl.setValidators(this.fcListValidation.getValidators('ClinicID'));
    this.clinicIDControl.setValue(this.listItem.SenderID);
    if (this.isFax) {
      this.clinicIDControl.disable();
    }

    // Name Control
    this.nameControl.setValidators(this.fcListValidation.getValidators('Name'));
    this.nameControl.setValue(this.listItem.Name);
    this.nameControl.disable();

    // Notes Control
    this.notesControl.setValidators(this.fcListValidation.getValidators('Notes'));
    this.notesControl.setValue(this.listItem.Notes);

    // Inactive Control
    this.inactiveControl.setValue(this.listItem.Inactive);

    // User Defined Control
    this.userDefinedControl.setValue(false);
    this.userDefinedControl.disable();

    this.validateForm();
  }

  public getFormValidationErrors(formGroup: FormGroupArray): number {
    return FcListValidationService.getFormValidationErrors(formGroup, this.isSubmitted);
  }

  public override save(): void {
    const values = this.form.getRawValue();
    const modifyItemDto: FcModifyPharmacyClearinghouseListItemDto = {
      ExternalID: values.externalId,
      Name: values.name,
      ClinicName: values.clinicName,
      SenderID: values.clinicID,
      Inactive: values.inactive,
      Notes: values.notes,
      UserDefined: values.userDefined,
      Providers: values.providers.providersUid,
      InactivateFaxOnly: values.managePharmacy.disableEPrescribe,
      InterfaceDetails: {
          TestMode: values.interfaceDetails.connectInTestMode,
          ProductionModeSettings: {
              GatewayUrl: values.interfaceDetails.gatewayUrl,
              GatewayLoginID: values.interfaceDetails.gatewayLoginID,
              GatewayPassword: values.interfaceDetails.password,
              MailboxUrl: values.interfaceDetails.mailboxUrl,
              MailboxName: values.interfaceDetails.mailboxName,
              MailboxPassword: values.interfaceDetails.mailboxPassword,
          },
          TestModeSettings: {
              GatewayUrl: values.interfaceDetails.testGatewayUrl,
              GatewayLoginID: values.interfaceDetails.testGatewayLoginID,
              GatewayPassword: values.interfaceDetails.testPassword,
              MailboxUrl: values.interfaceDetails.testMailboxUrl,
              MailboxName: values.interfaceDetails.testMailboxName,
              MailboxPassword: values.interfaceDetails.testMailboxPassword,
          }
      }
    };
    super.save<FcModifyPharmacyClearinghouseListItemDto>(modifyItemDto);
  }
}
